/**
 * Converts a Blob to a Base64 string.
 * @param {Blob} blob
 * @returns {Promise<string>} Base64 string
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      // The result is something like: "data:audio/webm;base64,AAAA..."
      // If you want only the base64 part, split by ',' and take the second part
      const result = reader.result
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Converte um ArrayBuffer ou Uint8Array (recebido de um endpoint) em um objeto File.
 * @param {ArrayBuffer|Uint8Array|Blob} data - Os dados binários recebidos do endpoint.
 * @param {string} fileName - O nome para o arquivo resultante.
 * @param {string} [mimeType] - O tipo MIME do arquivo (opcional).
 * @returns {File} O arquivo criado a partir dos dados.
 */
export function arrayBufferToFile(data, fileName, mimeType = '') {
  try {
    // Se já for um Blob, retorna diretamente
    if (data instanceof Blob) {
      return new File([data], fileName, { type: mimeType || data.type })
    }

    // Se for ArrayBuffer, converte para Uint8Array
    let uint8Array
    if (data instanceof Uint8Array) {
      uint8Array = data
    }
    else if (data instanceof ArrayBuffer) {
      uint8Array = new Uint8Array(data)
    }
    else {
      throw new TypeError('Invalid data type. Expected ArrayBuffer, Uint8Array, or Blob.')
    }

    // Determina o MIME type baseado na extensão do arquivo se não fornecido
    let finalMimeType = mimeType
    if (!finalMimeType && fileName) {
      const extension = fileName.toLowerCase().split('.').pop()
      switch (extension) {
        case 'ogg':
          finalMimeType = 'audio/ogg'
          break
        case 'webm':
          finalMimeType = 'video/webm'
          break
        case 'mp4':
          finalMimeType = 'video/mp4'
          break
        case 'wav':
          finalMimeType = 'audio/wav'
          break
        case 'mp3':
          finalMimeType = 'audio/mpeg'
          break
        default:
          finalMimeType = 'application/octet-stream'
      }
    }

    return new File([uint8Array], fileName, { type: finalMimeType })
  }
  catch (error) {
    console.error(error)
    return null
  }
}

/**
 * Converte um ArrayBuffer em um Blob para reprodução.
 * @param {ArrayBuffer|Uint8Array} data - Os dados binários.
 * @param {string} mimeType - O tipo MIME do arquivo.
 * @returns {Blob} O blob criado a partir dos dados.
 */
export function arrayBufferToBlob(data, mimeType) {
  // Se for ArrayBuffer, converte para Uint8Array
  let uint8Array
  if (data instanceof Uint8Array) {
    uint8Array = data
  }
  else if (data instanceof ArrayBuffer) {
    uint8Array = new Uint8Array(data)
  }
  else {
    throw new TypeError('Invalid data type. Expected ArrayBuffer or Uint8Array.')
  }

  return new Blob([uint8Array], { type: mimeType })
}

/**
 * Downloads a file in the browser.
 * @param {File|Blob} file - The file or blob to be downloaded.
 * @param {string} fileName - The name of the file to save as.
 */
export function downloadFile(file, fileName) {
  const url = URL.createObjectURL(file)
  const a = document.createElement('a')
  a.href = url
  const _fileName = fileName || file.name
  a.download = _fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Generates a slug from a string, removing accents, all non-letter characters, and replacing spaces with dashes.
 * @param {string} text - The text to be converted into a slug.
 * @returns {string} The generated slug.
 */
export function generateSlug(text) {
  if (!text || typeof text !== 'string')
    return ''
  return text
    .normalize('NFD') // separates accents from letters
    .replace(/[\u0300-\u036F]/g, '') // removes accents
    .replace(/[^a-z\s]/gi, '') // removes everything that is not a letter or space
    .trim()
    .replace(/\s+/g, '-') // replaces spaces with dashes
    .toLowerCase()
}
